import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { useBookingExtrasDeps } from './context';
import type {
  Deposit,
  FeedRun,
  Policy,
  PolicyOut,
  Recurrence,
  WaitlistDraft,
  WaitlistEntry,
  WaitlistPage,
} from './schemas';

/**
 * The TanStack Query layer: one key per §5 resource, and a mutation per
 * write that invalidates exactly what its write can change.
 *
 * **Every deposit write invalidates the whole feature**, not just the
 * one booking's key: applying a deposit writes a `payment` and
 * forfeiting one writes a voucher, and both change the day's close —
 * which is another feature's query and another tab's problem. Keeping
 * it to `all` here is the honest scope of what this module knows.
 *
 * Nothing here invalidates BP-09's report cache. The server does that,
 * through `deposit.applied` and `deposit.forfeited`.
 */
export const bookingExtraKeys = {
  all: ['booking-extras'] as const,
  deposit: (bookingId: string) => [...bookingExtraKeys.all, 'deposit', bookingId] as const,
  waitlist: (status: string | undefined) =>
    [...bookingExtraKeys.all, 'waitlist', status ?? 'all'] as const,
  mine: () => [...bookingExtraKeys.all, 'waitlist', 'mine'] as const,
  recurrence: (id: string) => [...bookingExtraKeys.all, 'recurrence', id] as const,
  feeds: () => [...bookingExtraKeys.all, 'feeds'] as const,
  policy: () => [...bookingExtraKeys.all, 'policy'] as const,
};

export function useDeposit(bookingId: string): UseQueryResult<Deposit | null> {
  const { api } = useBookingExtrasDeps();
  return useQuery({
    queryKey: bookingExtraKeys.deposit(bookingId),
    queryFn: () => api.readDeposit(bookingId),
  });
}

export function useWaitlist(status?: string): UseQueryResult<WaitlistPage> {
  const { api } = useBookingExtrasDeps();
  return useQuery({
    queryKey: bookingExtraKeys.waitlist(status),
    queryFn: () => api.listWaitlist(status),
  });
}

export function useMyWaitlist(): UseQueryResult<WaitlistPage> {
  const { api } = useBookingExtrasDeps();
  return useQuery({ queryKey: bookingExtraKeys.mine(), queryFn: () => api.listMine() });
}

/**
 * One rule, by id — and the empty string is "this booking has none".
 *
 * A sentinel rather than `string | null`, because a hook cannot be
 * called conditionally and `id ?? ''` inside the query function would
 * be a branch nothing can ever take: `enabled` already stopped it.
 */
export function useRecurrence(id: string): UseQueryResult<Recurrence> {
  const { api } = useBookingExtrasDeps();
  return useQuery({
    queryKey: bookingExtraKeys.recurrence(id),
    queryFn: () => api.readRecurrence(id),
    enabled: id !== '',
  });
}

export function useFeeds(): UseQueryResult<FeedRun[]> {
  const { api } = useBookingExtrasDeps();
  return useQuery({ queryKey: bookingExtraKeys.feeds(), queryFn: () => api.listFeeds() });
}

export function usePolicy(): UseQueryResult<PolicyOut> {
  const { api } = useBookingExtrasDeps();
  return useQuery({ queryKey: bookingExtraKeys.policy(), queryFn: () => api.readPolicy() });
}

function useInvalidateAll(): () => void {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: bookingExtraKeys.all });
  };
}

export function useRequestDeposit(
  bookingId: string,
): UseMutationResult<Deposit, Error, number | null> {
  const { api, newIdempotencyKey } = useBookingExtrasDeps();
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (amountPaise: number | null) =>
      api.requestDeposit(bookingId, amountPaise, newIdempotencyKey()),
    onSuccess: invalidate,
  });
}

export function useApplyDeposit(): UseMutationResult<
  Deposit,
  Error,
  { depositId: string; invoiceId: string }
> {
  const { api, newIdempotencyKey } = useBookingExtrasDeps();
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ depositId, invoiceId }: { depositId: string; invoiceId: string }) =>
      api.applyDeposit(depositId, invoiceId, newIdempotencyKey()),
    onSuccess: invalidate,
  });
}

export function useForfeitDeposit(): UseMutationResult<
  Deposit,
  Error,
  { depositId: string; reason: string }
> {
  const { api, newIdempotencyKey } = useBookingExtrasDeps();
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ depositId, reason }: { depositId: string; reason: string }) =>
      api.forfeitDeposit(depositId, reason, newIdempotencyKey()),
    onSuccess: invalidate,
  });
}

export function useJoinWaitlist(): UseMutationResult<WaitlistEntry, Error, WaitlistDraft> {
  const { api } = useBookingExtrasDeps();
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (draft: WaitlistDraft) => api.joinWaitlist(draft),
    onSuccess: invalidate,
  });
}

export function useCancelWaitlist(): UseMutationResult<void, Error, string> {
  const { api } = useBookingExtrasDeps();
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (entryId: string) => api.cancelWaitlist(entryId),
    onSuccess: invalidate,
  });
}

export function useJoinMyWaitlist(): UseMutationResult<WaitlistEntry, Error, WaitlistDraft> {
  const { api } = useBookingExtrasDeps();
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (draft: WaitlistDraft) => api.joinMine(draft),
    onSuccess: invalidate,
  });
}

export function useStartRecurrence(
  bookingId: string,
): UseMutationResult<Recurrence, Error, string> {
  const { api, newIdempotencyKey } = useBookingExtrasDeps();
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (rrule: string) => api.startRecurrence(bookingId, rrule, newIdempotencyKey()),
    onSuccess: invalidate,
  });
}

export function useStopRecurrence(): UseMutationResult<Recurrence, Error, string> {
  const { api } = useBookingExtrasDeps();
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (recurrenceId: string) => api.stopRecurrence(recurrenceId),
    onSuccess: invalidate,
  });
}

export function useSplitRecurrence(): UseMutationResult<
  Recurrence,
  Error,
  { recurrenceId: string; bookingId: string; rrule: string }
> {
  const { api } = useBookingExtrasDeps();
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (args: { recurrenceId: string; bookingId: string; rrule: string }) =>
      api.splitRecurrence(args.recurrenceId, args.bookingId, args.rrule),
    onSuccess: invalidate,
  });
}

export function useRegenerateFeeds(): UseMutationResult<FeedRun[], Error, void> {
  const { api } = useBookingExtrasDeps();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.regenerateFeeds(),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: bookingExtraKeys.feeds() });
    },
  });
}

export function useSavePolicy(): UseMutationResult<PolicyOut, Error, Policy> {
  const { api } = useBookingExtrasDeps();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (policy: Policy) => api.savePolicy(policy),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: bookingExtraKeys.policy() });
    },
  });
}
